/**
 * minutesPlayed — client-side minutes per player, derived from the event log
 * (EPIC-107, spec D1). Pure; never reads the backend `minutes_played` field.
 *
 * Deliberately differs from backend `minutes_played` for a sent-off player:
 * a `Card` event whose `data.card_type` contains "red" and not "cancel"
 * (the same rule `usePlayerStats` uses) ends the stint at the card's clock.
 * A second yellow is `Yellow (Second)` followed by a separate `Red`, so the
 * `Red` event is what ends it.
 *
 * Rules:
 *  - starters (`is_starter`) are on from 0 seconds;
 *  - `Substitution` with `data.player_off_id` ends a stint, `data.player_on_id`
 *    starts one;
 *  - an open stint ends at `currentClockSeconds` (live) or the final match
 *    clock (finished);
 *  - minutes = floor(seconds / 60), but any player with > 0 seconds shows >= 1;
 *  - penalty shootout events never count (and never move a stint boundary).
 *
 * Clock frame (EPIC-107 fe-fix): `match_clock` is stamped in the BROADCAST
 * frame, which keeps stoppage (first half 47:00 > second half's 46:00) and
 * restarts per period. Boundaries are therefore ordered by (period, clock)
 * and every clock is lifted onto one continuous timeline by adding the
 * earlier periods' overrun past their nominal end (45/90/105/120:00).
 * `currentClockSeconds` must be in that same broadcast frame; it is lifted
 * with the offset of the latest period seen in `events` (or `currentPeriod`).
 *
 * Finished matches (EPIC-107 fe-fix2): in COMPLETED/PENALTIES the broadcast
 * main clock is the EFFECTIVE total only (`resolveBroadcastArgument`), a
 * different frame from the stamped event clocks, so `currentClockSeconds` is
 * NOT used as the end. `options.matchFinished` ends open stints at the end of
 * the final regulation/extra period in the lifted broadcast frame:
 * `baseline(f) + (matchTotalSeconds - global_start(f)) + earlier overrun`,
 * where `matchTotalSeconds` = match_time_seconds + ineffective_time_seconds
 * (the banked global total) and `global_start(f)` is the final period's
 * `period_timestamps` start (same frame, same guards as below; overrun past
 * nominal outside [0, 30:00] is rejected). Otherwise the latest lifted
 * non-shootout event clock. The end is always floored at that event clock.
 * Shootout time is never counted.
 *
 * Earlier-period length: the overrun of period q is the larger of the
 * event-based estimate (max stamped clock past nominal end) and, when
 * `options.periodTimestamps` carries `global_start_seconds` for period q+1,
 * the whistle-derived value `start(q+1) - start(q) - regulation(q)`.
 * `global_start_seconds` = backend effective+ineffective total at period
 * start (VAR/timeout live inside ineffective), frozen across halftime, and
 * the broadcast period elapsed is that same global delta, so
 * stamped end of q = nominal baseline + (start(q+1) - start(q)). A derived
 * overrun outside [0, 30:00] is treated as unreliable (double-counted or
 * corrupt) and ignored in favour of the estimate.
 */
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Player } from "../types";
import { parseClockToSeconds } from "../lib/clockHelpers";
import { isShootoutKick } from "./penaltyShootout";

export interface MinutesPlayedLineups {
  home: Pick<Player, "id" | "is_starter">[];
  away: Pick<Player, "id" | "is_starter">[];
}

const isRedCard = (event: MatchEvent): boolean => {
  if (event.type !== "Card") return false;
  const cardType = String(event.data?.card_type ?? "").toLowerCase();
  return cardType.includes("red") && !cardType.includes("cancel");
};

const NOMINAL_PERIOD_END_SECONDS: Record<number, number> = {
  1: 45 * 60,
  2: 90 * 60,
  3: 105 * 60,
  4: 120 * 60,
};

const timeOf = (event: MatchEvent): number => {
  const t = Date.parse(event.timestamp);
  return Number.isFinite(t) ? t : 0;
};

export interface MinutesPlayedOptions {
  /** COMPLETED/PENALTIES: end at the final period's end, not at `currentClockSeconds`. */
  matchFinished?: boolean;
  /** match_time_seconds + ineffective_time_seconds (global total banked at the end). */
  matchTotalSeconds?: number;
  /** `match.period_timestamps`; refines earlier periods' real length. */
  periodTimestamps?: Record<string, { global_start_seconds?: number }>;
}

const MAX_RELIABLE_OVERRUN_SECONDS = 30 * 60;

export function computeMinutesPlayed(
  events: MatchEvent[],
  lineups: MinutesPlayedLineups,
  currentClockSeconds: number,
  currentPeriod?: number,
  options: MinutesPlayedOptions = {},
): Record<string, number> {
  const rawEnd = Number.isFinite(currentClockSeconds)
    ? Math.max(0, currentClockSeconds)
    : 0;

  // Effective period per event: its own `period`, else carried forward in
  // timestamp order from the nearest earlier event that has one (default 1).
  const periodOf = new Map<MatchEvent, number>();
  let carried = 1;
  [...events]
    .map((event, index) => ({ event, index }))
    .sort((a, b) => timeOf(a.event) - timeOf(b.event) || a.index - b.index)
    .forEach(({ event }) => {
      if (typeof event.period === "number" && event.period >= 1) {
        carried = event.period;
      }
      periodOf.set(event, carried);
    });

  // Overrun (stoppage) of each period = its max stamped clock past nominal end.
  const maxClockByPeriod = new Map<number, number>();
  let maxPeriod = 1;
  for (const event of events) {
    if (isShootoutKick(event)) continue;
    const p = periodOf.get(event) ?? 1;
    if (p > 4) continue;
    maxPeriod = Math.max(maxPeriod, p);
    const c = parseClockToSeconds(event.match_clock);
    if (Number.isFinite(c) && c > (maxClockByPeriod.get(p) ?? 0)) {
      maxClockByPeriod.set(p, c);
    }
  }
  const startOf = (q: number): number | undefined => {
    const v = options.periodTimestamps?.[String(q)]?.global_start_seconds;
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
    return q === 1 ? 0 : undefined;
  };
  const whistleOverrun = (q: number, nominal: number): number => {
    const from = startOf(q);
    const to = startOf(q + 1);
    if (from === undefined || to === undefined) return 0;
    const regulation = nominal - (NOMINAL_PERIOD_END_SECONDS[q - 1] ?? 0);
    const overrun = to - from - regulation;
    return overrun >= 0 && overrun <= MAX_RELIABLE_OVERRUN_SECONDS
      ? overrun
      : 0;
  };
  const offsetFor = (period: number): number => {
    let offset = 0;
    for (let q = 1; q < period; q += 1) {
      const nominal = NOMINAL_PERIOD_END_SECONDS[q];
      if (nominal === undefined) continue;
      offset += Math.max(
        0,
        (maxClockByPeriod.get(q) ?? 0) - nominal,
        whistleOverrun(q, nominal),
      );
    }
    return offset;
  };
  const endPeriod = Math.max(maxPeriod, currentPeriod ?? 1);
  let endClock: number;
  if (options.matchFinished) {
    let lastEvent = 0;
    for (const event of events) {
      if (isShootoutKick(event)) continue;
      if ((periodOf.get(event) ?? 1) > 4) continue;
      const c = parseClockToSeconds(event.match_clock);
      if (Number.isFinite(c)) {
        lastEvent = Math.max(
          lastEvent,
          c + offsetFor(periodOf.get(event) ?? 1),
        );
      }
    }
    let periodEnd = 0;
    const total = options.matchTotalSeconds;
    const startF = startOf(endPeriod);
    const nominalF = NOMINAL_PERIOD_END_SECONDS[endPeriod];
    if (
      typeof total === "number" &&
      Number.isFinite(total) &&
      startF !== undefined &&
      nominalF !== undefined &&
      total >= startF
    ) {
      const baseline = NOMINAL_PERIOD_END_SECONDS[endPeriod - 1] ?? 0;
      const stampedEnd = baseline + (total - startF);
      if (stampedEnd - nominalF <= MAX_RELIABLE_OVERRUN_SECONDS) {
        periodEnd = stampedEnd + offsetFor(endPeriod);
      }
    }
    endClock = Math.max(lastEvent, periodEnd);
    if (endClock === 0) endClock = rawEnd; // nothing better is known
  } else {
    endClock = rawEnd + offsetFor(endPeriod);
  }
  const lifted = (event: MatchEvent): number =>
    parseClockToSeconds(event.match_clock) +
    offsetFor(periodOf.get(event) ?? 1);

  const openSince = new Map<string, number>();
  const totals = new Map<string, number>();
  const sentOff = new Set<string>();

  const close = (id: string, at: number) => {
    const start = openSince.get(id);
    if (start === undefined) return;
    openSince.delete(id);
    totals.set(id, (totals.get(id) ?? 0) + Math.max(0, at - start));
  };
  const open = (id: string, at: number) => {
    if (sentOff.has(id) || openSince.has(id)) return;
    openSince.set(id, at);
    if (!totals.has(id)) totals.set(id, 0);
  };

  for (const p of [...lineups.home, ...lineups.away]) {
    if (p.is_starter) open(p.id, 0);
  }

  const ordered = events
    .map((event, index) => ({ event, index }))
    .filter(
      ({ event }) =>
        (event.type === "Substitution" || event.type === "Card") &&
        !isShootoutKick(event),
    )
    .sort((a, b) => {
      const pa = periodOf.get(a.event) ?? 1;
      const pb = periodOf.get(b.event) ?? 1;
      if (pa !== pb) return pa - pb;
      const ca = parseClockToSeconds(a.event.match_clock);
      const cb = parseClockToSeconds(b.event.match_clock);
      return ca !== cb ? ca - cb : a.index - b.index;
    });

  for (const { event } of ordered) {
    const at = Math.min(lifted(event), endClock);
    if (event.type === "Substitution") {
      const off = event.data?.player_off_id;
      const on = event.data?.player_on_id;
      if (off) close(String(off), at);
      if (on) open(String(on), at);
    } else if (isRedCard(event) && event.player_id) {
      close(event.player_id, at);
      sentOff.add(event.player_id);
    }
  }

  for (const id of Array.from(openSince.keys())) close(id, endClock);

  const result: Record<string, number> = {};
  totals.forEach((seconds, id) => {
    result[id] = seconds > 0 ? Math.max(1, Math.floor(seconds / 60)) : 0;
  });
  return result;
}
